import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { isFalabellaEnabledCountry } from '@/constants/falabella';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { IResBalanceOK, IResProfileOK } from '@/types/parcel/userData';
import { UserPlugin } from '@/types/plugins/mlFulfill';

import type { IFalabellaPluginDetail } from '@/types/plugins/falabella';

type ParcelThunkState = {
  parcel: {
    profile: { loading: boolean; data: IResProfileOK['data'] | null };
    fulfillAccess: { loading: boolean; checked: boolean };
    mlParcelAccess: { checked: boolean };
    envioFlexAccess: { checked: boolean };
    mlFulfillAccess: { checked: boolean };
    falabellaAccess: { checked: boolean };
  };
};

export const getProfile = createAsyncThunk<IResProfileOK, { forceRefresh?: boolean } | void>(
  'parcel/profile',
  async (_params, { rejectWithValue }) => {
    try {
      const http = initializeHttpProvider();
      const res = await retryWithBackoff(() => http.userData.profile(), 5, 1000);

      // Si es México y prepago, obtener token UPS (sin bloquear si falla)
      if (
        res.success &&
        'data' in res &&
        !Array.isArray(res.data) &&
        res.data.country === 'MEX' &&
        res.data.configuration.paymentType === 'PREPAID'
      ) {
        try {
          const upsToken = await http.ups.token({
            apiKey: res.data.credentials.legacyApiKey,
            clientId: res.data.credentials.clientId,
            clientSecret: res.data.credentials.clientSecret,
            email: res.data.profile.email,
          });

          if (upsToken.success && 'data' in upsToken && typeof upsToken.data === 'string') {
            localStorage.setItem('upsToken', upsToken.data);
          }
        } catch {
          // Si falla el token de UPS, no afecta el profile
          console.error('Error al obtener token UPS');
        }
      }

      return res as IResProfileOK;
    } catch {
      toast.error('Error al obtener el perfil');
      return rejectWithValue('Error al obtener el perfil');
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState() as ParcelThunkState;

      // Si forceRefresh es true, permitir la ejecución (solo prevenir si ya está cargando)
      if (params && typeof params === 'object' && 'forceRefresh' in params && params.forceRefresh) {
        return !state.parcel.profile.loading;
      }

      // Comportamiento por defecto: No ejecutar si ya está cargando o si ya hay data
      return !(state.parcel.profile.loading || !!state.parcel.profile.data);
    },
  },
);

const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 5,
  baseDelay: number = 1000,
): Promise<T> => {
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      return await fn();
    } catch (error) {
      attempt++;

      if (attempt >= maxRetries) {
        throw error;
      }

      const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), 5000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error('Unexpected end of retry function');
};

export const getBalance = createAsyncThunk<IResBalanceOK>(
  'parcel/balance',
  async (_, { rejectWithValue }) => {
    try {
      const http = initializeHttpProvider();
      const res = await retryWithBackoff(() => http.userData.balance(), 5, 1000);
      return res as IResBalanceOK;
    } catch {
      toast.error('Error al obtener el balance');
      return rejectWithValue('Error al obtener el balance');
    }
  },
);

/**
 * Checks if the user has an active ML Fulfill plugin.
 */
export const checkMLFulfillAccess = createAsyncThunk<boolean, void>(
  'parcel/checkMLFulfillAccess',
  async (_, { getState }) => {
    const state = getState() as ParcelThunkState;
    const clientId = state.parcel.profile.data?.credentials?.clientId;
    if (!clientId) return false;
    try {
      const http = initializeHttpProvider();
      const response = await http.mlFulfill.getUserPlugins({ clientId });
      if ('data' in response && response.success && Array.isArray(response.data)) {
        return (response.data as UserPlugin[]).some(
          (p) => p.platform.toLowerCase().includes('fulfillment') && p.active,
        );
      }
      return false;
    } catch {
      return false;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as ParcelThunkState;
      return !state.parcel.mlFulfillAccess.checked && !!state.parcel.profile.data;
    },
  },
);

/**
 * Checks if the user has an active EnvioFlex (Mercado Envíos) plugin.
 */
export const checkEnvioFlexAccess = createAsyncThunk<boolean, void>(
  'parcel/checkEnvioFlexAccess',
  async (_, { getState }) => {
    const state = getState() as ParcelThunkState;
    const clientId = state.parcel.profile.data?.credentials?.clientId;
    if (!clientId) return false;
    try {
      const http = initializeHttpProvider();
      const response = await http.envioFlex.getUserPlugins({ clientId });
      if ('data' in response && response.success && Array.isArray(response.data)) {
        return (response.data as UserPlugin[]).some(
          (p) =>
            p.platform.toLowerCase().includes('mercado') &&
            !p.platform.toLowerCase().includes('fulfillment') &&
            !p.platform.toLowerCase().includes('parcel') &&
            p.active,
        );
      }
      return false;
    } catch {
      return false;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as ParcelThunkState;
      return !state.parcel.envioFlexAccess.checked && !!state.parcel.profile.data;
    },
  },
);

/**
 * Checks if the user has an active ML Parcel plugin.
 */
export const checkMLParcelAccess = createAsyncThunk<boolean, void>(
  'parcel/checkMLParcelAccess',
  async (_, { getState }) => {
    const state = getState() as ParcelThunkState;
    const clientId = state.parcel.profile.data?.credentials?.clientId;
    if (!clientId) return false;
    try {
      const http = initializeHttpProvider();
      const response = await http.envioFlex.getUserPlugins({ clientId });
      if ('data' in response && response.success && Array.isArray(response.data)) {
        const userPlugins = response.data as UserPlugin[];
        const parcelPlugin = userPlugins.find(
          (plugin) => plugin.platform === 'mercado_envios_99m_parcel' && plugin.active,
        );
        return !!parcelPlugin;
      }
      return false;
    } catch {
      return false;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as ParcelThunkState;
      return !state.parcel.mlParcelAccess.checked && !!state.parcel.profile.data;
    },
  },
);

/**
 * Checks if the user has an active Falabella plugin with corresponding configuration (enabled countries only).
 */
export const checkFalabellaAccess = createAsyncThunk<boolean, void>(
  'parcel/checkFalabellaAccess',
  async (_, { getState }) => {
    const state = getState() as ParcelThunkState;
    const profile = state.parcel.profile.data;
    const clientId = profile?.credentials?.clientId;
    const country = profile?.country;

    if (!clientId || !isFalabellaEnabledCountry(country)) return false;

    try {
      const http = initializeHttpProvider();
      const response = await http.falabella.getUserPlugins({ clientId });
      if ('data' in response && response.success && Array.isArray(response.data)) {
        const userPlugins = response.data as IFalabellaPluginDetail[];
        const falabellaPlugin = userPlugins.find(
          (plugin) => plugin.platform?.toLowerCase() === 'falabella' && plugin.active,
        );
        return !!falabellaPlugin;
      }
      return false;
    } catch {
      return false;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as ParcelThunkState;
      return !state.parcel.falabellaAccess.checked && !!state.parcel.profile.data;
    },
  },
);

/**
 * Checks if the user has access to Fulfill99.
 */
export const checkFulfillAccess = createAsyncThunk<boolean, void>(
  'parcel/checkFulfillAccess',
  async (_, { getState }) => {
    const state = getState() as ParcelThunkState;
    const profile = state.parcel.profile.data;
    const hasServiceEnabled = profile?.configuration?.services?.fulfill99 === true;

    if (!hasServiceEnabled) {
      return false;
    }

    const clientId = profile?.credentials?.clientId;

    if (!clientId) {
      console.error('[checkFulfillAccess] ClientId no disponible para verificar acceso a Fulfill');
      return false;
    }

    try {
      const http = initializeHttpProvider();
      const response = await retryWithBackoff(
        () => http.fulfillUsers.checkUserExists(clientId),
        3,
        1000,
      );

      if (response.success) {
        return response.data.exists;
      }

      return false;
    } catch (error) {
      console.error('[checkFulfillAccess] Error al verificar acceso en Fulfill BO:', error);
      // fail-closed: deny access if the service is unreachable or returns an error
      return false;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as ParcelThunkState;
      const { loading, checked } = state.parcel.fulfillAccess;

      if (loading || checked) return false;

      return !!state.parcel.profile.data;
    },
  },
);
